import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { COLOR_STORY, COLOR_BUG, COLOR_TASK } from './shared';
import { METRIC_VIEW_CHART_HEIGHT } from '@/lib/metric-view/responsive-thresholds';
import { type IBucketAxisProps } from '@/lib/metric-view/bucket-format';

export interface ITrendPoint {
  week: string;
  Story: number;
  Bug: number;
  Task: number;
}

const VISIBLE_TYPES: ReadonlyArray<{ key: 'Story' | 'Bug' | 'Task'; color: string }> = [
  { key: 'Story', color: COLOR_STORY },
  { key: 'Bug', color: COLOR_BUG },
  { key: 'Task', color: COLOR_TASK },
];

interface ITrendLineChartProps {
  data: ITrendPoint[];
  activeType: 'all' | 'story' | 'bug' | 'task';
  height?: number;
  /**
   * Optional. When supplied: x-axis ticks use the helper's `tickFormatter`
   * (current bucket → `"5/18 – 21"`, others → `"5/18"`), the tooltip header
   * gains a `"(so far)"` suffix on the current bucket, and a dashed "Today"
   * `<ReferenceLine>` is drawn iff `todayLineDate !== null`. See
   * `bucket-format.ts`.
   */
  axis?: IBucketAxisProps;
}

export function TrendLineChart({ data, activeType, height = METRIC_VIEW_CHART_HEIGHT, axis }: Readonly<ITrendLineChartProps>) {
  const visible = activeType === 'all'
    ? VISIBLE_TYPES
    : VISIBLE_TYPES.filter((t) => t.key.toLowerCase() === activeType);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis
          dataKey="week"
          tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          axisLine={{ stroke: 'var(--color-border)' }}
          tickFormatter={axis?.tickFormatter}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          axisLine={{ stroke: 'var(--color-border)' }}
        />
        <Tooltip labelFormatter={axis?.tooltipLabelFormatter} />
        {axis?.todayLineDate && (
          <ReferenceLine
            x={axis.todayLineDate}
            stroke="var(--color-muted-foreground)"
            strokeDasharray="4 3"
            strokeOpacity={0.7}
            ifOverflow="extendDomain"
            label={{
              value: 'Today',
              position: 'top',
              fill: 'var(--color-muted-foreground)',
              fontSize: 10,
            }}
          />
        )}
        {visible.map((t) => (
          <Line
            key={t.key}
            type="monotone"
            dataKey={t.key}
            stroke={t.color}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
